import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { InfoIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getLikelihoodStatus } from "@/lib/assessment/scoring";

interface SuccessLikelihoodProps {
  likelihood: number;
  pathwayTitle: string;
}

const SuccessLikelihood = ({ likelihood, pathwayTitle }: SuccessLikelihoodProps) => {
  const { status, color, description } = getLikelihoodStatus(likelihood);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Success Likelihood
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <InfoIcon className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Calculated based on your profile strength, industry demand, and historical success rates
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <Badge variant="secondary" className={color}>
            {status.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Chance of Success</span>
            <span className={color}>{likelihood}%</span>
          </div>
          <Progress 
            value={likelihood} 
            className="h-2"
            indicatorClassName={
              status === 'high' ? 'bg-green-500' :
              status === 'medium' ? 'bg-yellow-500' :
              'bg-red-500'
            }
          />
        </div>

        <div className="space-y-2">
          <h4 className="font-medium">Pathway Analysis</h4>
          <p className="text-sm text-muted-foreground">
            For the {pathwayTitle} pathway, your profile shows a {likelihood}% likelihood of success. {description}
          </p>
        </div>

        <div className="bg-accent/5 rounded-lg p-4">
          <h4 className="font-medium mb-2">What This Means</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
              <span>This assessment is based on your profile strength and historical data</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
              <span>Consider focusing on areas where your scores are lower to improve your chances</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
              <span>Book a consultation for personalized guidance on improving your application</span>
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default SuccessLikelihood;